import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { isAssocie, isSuperAdmin } from './lib/permissions';
import AppLayout from './components/layout/AppLayout';
import Toaster from './components/Toaster';
import ConfirmDialog from './components/ConfirmDialog';
import ListSkeleton from './components/Skeleton';
// Statique : premier rendu (login/callback) + portail client (exports nommés).
import LoginPage from './pages/LoginPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import PortalLoginPage from './pages/PortalLoginPage';
import PortalActivatePage from './pages/PortalActivatePage';
import PortalLayout, { PortalDashboard, PortalDossiers, PortalDossierDetail, PortalFactures, PortalFactureDetail, PortalContrats } from './pages/PortalLayout';
import './App.css';

// Code-splitting par route : chaque page = un chunk chargé à la demande.
// Bundle initial réduit ; navigation inchangée (fallback skeleton instantané).
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const DossiersPage = lazy(() => import('./pages/DossiersPage'));
const DossierDetailPage = lazy(() => import('./pages/DossierDetailPage'));
const ClientsListPage = lazy(() => import('./pages/ClientsListPage'));
const ClientDetailPage = lazy(() => import('./pages/ClientDetailPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const ContractsPage = lazy(() => import('./pages/ContractsPage'));
const ContratDetailPage = lazy(() => import('./pages/ContratDetailPage'));
const DeadlinesPage = lazy(() => import('./pages/DeadlinesPage'));
const TachesPage = lazy(() => import('./pages/TachesPage'));
const TempsPage = lazy(() => import('./pages/TempsPage'));
const AIChatPage = lazy(() => import('./pages/AIChatPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const FinancePage = lazy(() => import('./pages/FinancePage'));
const DepensesPage = lazy(() => import('./pages/DepensesPage'));
const EmployesPage = lazy(() => import('./pages/EmployesPage'));
const PaiePage = lazy(() => import('./pages/PaiePage'));
const RechercheDocumentsPage = lazy(() => import('./pages/RechercheDocumentsPage'));
// La GED : on entre par le client, on dépose, on classe. La recherche plein
// texte reste accessible sur son propre écran, pour quand on sait quoi chercher.
const GedPage = lazy(() => import('./pages/GedPage'));
const IntervenantsPage = lazy(() => import('./pages/IntervenantsPage'));
const ContactsPage = lazy(() => import('./pages/ContactsPage'));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'));
const PortalAdminPage = lazy(() => import('./pages/PortalAdminPage'));
const SuperAdminPage = lazy(() => import('./pages/SuperAdminPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const DelaisCataloguePage = lazy(() => import('./pages/DelaisCataloguePage'));
const CourriersPage = lazy(() => import('./pages/CourriersPage'));

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen" style={{background:'var(--bg-primary)'}}><div className="text-[#C9A227]">Chargement…</div></div>;
  if (!user) return <Navigate to="/login" />;
  return children;
}

// Finance & administration : réservé aux associés. Redirige les autres vers l'accueil.
function RequireAssocie({ children }) {
  const { user } = useAuth();
  if (!isAssocie(user?.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  const isSuper = isSuperAdmin(user?.role, user?.is_super_admin);
  return (
    <Suspense fallback={<div className="p-6" style={{background:'var(--bg-primary)', minHeight:'100vh'}}><ListSkeleton kpi={0} rows={5} /></div>}>
    <Routes>
      {/* Routes publiques (non protégées) */}
      <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
      {/* Route de callback OAuth - DOIT être publique pour intercepter le token */}
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      {/* Portail Client (routes publiques indépendantes) */}
      <Route path="/portail/login" element={<PortalLoginPage />} />
      <Route path="/portail/activer" element={<PortalActivatePage />} />
      <Route path="/portail" element={<PortalLayout />}>
        <Route index element={<PortalDashboard />} />
        <Route path="dossiers" element={<PortalDossiers />} />
        <Route path="dossiers/:id" element={<PortalDossierDetail />} />
        <Route path="factures" element={<PortalFactures />} />
        <Route path="factures/:id" element={<PortalFactureDetail />} />
        <Route path="contrats" element={<PortalContrats />} />
      </Route>
      
      {/* Routes protégées (cabinet) */}
      <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={isSuper ? <Navigate to="/super-admin" replace /> : <DashboardPage />} />
        <Route path="dossiers" element={<DossiersPage />} />
        <Route path="dossiers/:id" element={<DossierDetailPage />} />
        <Route path="ged" element={<GedPage />} />
        <Route path="recherche-documents" element={<RechercheDocumentsPage />} />
        <Route path="clients" element={<ClientsListPage />} />
        <Route path="clients/:id" element={<ClientDetailPage />} />
        <Route path="calendrier" element={<CalendarPage />} />
        <Route path="facturation" element={<RequireAssocie><BillingPage /></RequireAssocie>} />
        <Route path="contrats" element={<ContractsPage />} />
        <Route path="contrats/:id" element={<ContratDetailPage />} />
        <Route path="echeances" element={<DeadlinesPage />} />
        <Route path="catalogue-delais" element={<DelaisCataloguePage />} />
        <Route path="courriers" element={<CourriersPage />} />
        <Route path="taches" element={<TachesPage />} />
        <Route path="temps" element={<TempsPage />} />
        <Route path="intervenants" element={<IntervenantsPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="chat" element={<AIChatPage />} />
        <Route path="finance" element={<RequireAssocie><FinancePage /></RequireAssocie>} />
        <Route path="rapports" element={<RequireAssocie><ReportsPage /></RequireAssocie>} />
        <Route path="depenses" element={<RequireAssocie><DepensesPage /></RequireAssocie>} />
        <Route path="personnel" element={<RequireAssocie><EmployesPage /></RequireAssocie>} />
        <Route path="paie" element={<RequireAssocie><PaiePage /></RequireAssocie>} />
        <Route path="modeles" element={<TemplatesPage />} />
        <Route path="portail-admin" element={<RequireAssocie><PortalAdminPage /></RequireAssocie>} />
        <Route path="super-admin" element={<SuperAdminPage />} />
        <Route path="equipe" element={<TeamPage />} />
        {/* Filet de sécurité : toute URL inconnue sous le shell → accueil
            (évite l'écran blanc sur un lien mal tapé ou un ancien favori). */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>

      {/* Filet global : URL inconnue hors shell → accueil (ProtectedRoute
          renverra vers /login si la session n'est pas ouverte). */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AppRoutes />
          <Toaster />
          <ConfirmDialog />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}
